import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { exportarPedidosDomicilioExcel, getPedidosDomicilio } from "../../services/api";
import showToast from "../../services/toast";
import {
  crearFiltrosPorDefecto,
  ESTADOS_TEROS,
  estadoTeros,
  ETIQUETAS_FILTROS,
  formatCurrency,
  formatDate,
  formatQuantity,
  limpiarFiltros,
  nombreCliente,
  numeroVisible,
} from "./utils";

const AVISO_REVISION =
  "Hay más de un pedido igual en Teros. Resolvelo desde el admin de Django antes de despacharlo.";

const PedidosDomicilio = () => {
  const filtrosPorDefecto = useMemo(() => crearFiltrosPorDefecto(), []);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [modalPedido, setModalPedido] = useState(null);
  const [filtros, setFiltros] = useState(filtrosPorDefecto);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosPorDefecto);
  const [errorValidacion, setErrorValidacion] = useState("");
  const [exportando, setExportando] = useState(false);
  const primeraCarga = useRef(true);

  const cargar = useCallback(async (filtrosActuales) => {
    if (primeraCarga.current) setLoading(true);
    else setIsFetching(true);
    setError(null);
    try {
      const respuesta = await getPedidosDomicilio(filtrosActuales);
      setPedidos(Array.isArray(respuesta.data) ? respuesta.data : []);
    } catch (err) {
      setError(err);
    } finally {
      if (primeraCarga.current) {
        setLoading(false);
        primeraCarga.current = false;
      } else {
        setIsFetching(false);
      }
    }
  }, []);

  useEffect(() => {
    cargar(filtrosAplicados);
  }, [filtrosAplicados, cargar]);

  const cambiarFiltro = ({ target: { name, value } }) => {
    setErrorValidacion("");
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const buscar = () => {
    const limpios = limpiarFiltros(filtros);
    if (limpios.fecha_desde && limpios.fecha_hasta && limpios.fecha_desde > limpios.fecha_hasta) {
      setErrorValidacion("La fecha desde no puede ser mayor que la fecha hasta.");
      return;
    }
    setErrorValidacion("");
    setFiltrosAplicados(limpios);
  };

  const limpiar = () => {
    setErrorValidacion("");
    setFiltros(filtrosPorDefecto);
    setFiltrosAplicados(filtrosPorDefecto);
  };

  const buscarConEnter = (event) => {
    if (event.key === "Enter") buscar();
  };

  const exportarExcel = async () => {
    setExportando(true);
    try {
      const respuesta = await exportarPedidosDomicilioExcel(filtrosAplicados);
      const blob = new Blob([respuesta.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `pedidos_delivery_${filtrosAplicados.fecha_desde || "inicio"}_${filtrosAplicados.fecha_hasta || "fin"}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      showToast.error("No se pudo exportar el Excel. Intentá de nuevo.");
    } finally {
      setExportando(false);
    }
  };

  const filtrosActivos = useMemo(
    () => Object.entries(filtrosAplicados).filter(([, valor]) => Boolean(valor)),
    [filtrosAplicados]
  );

  const valorFiltroLegible = (clave, valor) =>
    clave === "estado" ? estadoTeros(valor).etiqueta : valor;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container style={{ marginLeft: "0", maxWidth: "100%" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={1.5}
        mb={3}
      >
        <Box>
          <Typography variant="h4" sx={{ mt: 0, fontWeight: 700 }}>
            Delivery a domicilio - Pedidos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pedidos de la web nueva. Los de la web anterior siguen en su propia pantalla.
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 2, minWidth: { xs: "100%", md: "auto" } }}>
          <Typography variant="body2" color="text.secondary">
            Resultados
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {pedidos.length}
            </Typography>
            {isFetching && <CircularProgress size={18} />}
          </Stack>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{ mb: 3, p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider", borderRadius: 2, backgroundColor: "#fff" }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtros
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Combiná los criterios para acotar la búsqueda de pedidos.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Nro. pedido o referencia"
                name="numero"
                value={filtros.numero}
                onChange={cambiarFiltro}
                onKeyDown={buscarConEnter}
                placeholder="Ej. 177300 o delivery-523"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Documento"
                name="cedula"
                value={filtros.cedula}
                onChange={cambiarFiltro}
                onKeyDown={buscarConEnter}
                placeholder="Cédula o RUC"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Teléfono"
                name="telefono"
                value={filtros.telefono}
                onChange={cambiarFiltro}
                onKeyDown={buscarConEnter}
                placeholder="Ej. 0981 123 456"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField select fullWidth label="Estado" name="estado" value={filtros.estado} onChange={cambiarFiltro}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(ESTADOS_TEROS).map(([valor, { etiqueta }]) => (
                  <MenuItem key={valor} value={valor}>
                    {etiqueta}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Fecha desde"
                name="fecha_desde"
                type="date"
                value={filtros.fecha_desde}
                onChange={cambiarFiltro}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Fecha hasta"
                name="fecha_hasta"
                type="date"
                value={filtros.fecha_hasta}
                onChange={cambiarFiltro}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" onClick={limpiar} disabled={isFetching}>
              Limpiar
            </Button>
            <Button variant="contained" onClick={buscar} disabled={isFetching}>
              Buscar
            </Button>
            <Button variant="outlined" color="success" onClick={exportarExcel} disabled={isFetching || exportando}>
              {exportando ? "Exportando..." : "Exportar Excel"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {errorValidacion && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {errorValidacion}
        </Alert>
      )}

      {filtrosActivos.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {`Filtros activos: ${filtrosActivos
            .map(([clave, valor]) => `${ETIQUETAS_FILTROS[clave] || clave}: ${valorFiltroLegible(clave, valor)}`)
            .join(" | ")}`}
        </Alert>
      )}

      {error ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="50vh">
          <ReportProblemOutlinedIcon style={{ fontSize: 50, color: "gray" }} />
          <Typography variant="h6" color="textSecondary">
            Error al cargar los pedidos.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verificá los filtros aplicados e intentá nuevamente.
          </Typography>
        </Box>
      ) : pedidos.length === 0 ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="50vh">
          <HelpOutlineIcon style={{ fontSize: 50, color: "gray" }} />
          <Typography variant="h6" color="textSecondary">
            No hay pedidos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Probá ajustar o limpiar los filtros para ampliar la búsqueda.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid lightgray", borderRadius: "8px", backgroundColor: "#fff", overflowX: "auto", position: "relative" }}
        >
          {isFetching && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
          <Table sx={{ minWidth: 1080 }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: "bold" }}>Pedido</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Fecha</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Cliente</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Documento</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Teléfono</TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Entrega</TableCell>
                <TableCell style={{ fontWeight: "bold" }} align="right">
                  Total
                </TableCell>
                <TableCell style={{ fontWeight: "bold" }}>Estado</TableCell>
                <TableCell style={{ fontWeight: "bold" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pedidos.map((pedido) => {
                const estado = estadoTeros(pedido.estado_teros);
                return (
                  <TableRow key={pedido.id} sx={{ backgroundColor: "#fff", "&:hover": { backgroundColor: "#f0f0f0" } }}>
                    <TableCell>{numeroVisible(pedido)}</TableCell>
                    <TableCell>{formatDate(pedido.fecha_creacion)}</TableCell>
                    <TableCell>{nombreCliente(pedido)}</TableCell>
                    <TableCell>{pedido.cliente?.documento || "-"}</TableCell>
                    <TableCell>{pedido.cliente?.telefono || "-"}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {pedido.direccion?.nombre || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pedido.direccion?.direccion || ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(pedido.total)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={estado.etiqueta} color={estado.color} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => setModalPedido(pedido)} size="small" title="Ver pedido">
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(modalPedido)}
        onClose={() => setModalPedido(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        {modalPedido && (
          <>
            <DialogTitle>
              Pedido {numeroVisible(modalPedido)}
              {modalPedido.nropedido > 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({modalPedido.referencia})
                </Typography>
              )}
            </DialogTitle>
            <DialogContent dividers>
              {modalPedido.estado_teros === "revision" && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {AVISO_REVISION}
                </Alert>
              )}

              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Cliente
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Nombre:</strong> {nombreCliente(modalPedido)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Documento:</strong>{" "}
                  {modalPedido.cliente?.tipo_documento ? `${modalPedido.cliente.tipo_documento} ` : ""}
                  {modalPedido.cliente?.documento || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Teléfono:</strong> {modalPedido.cliente?.telefono || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Email:</strong> {modalPedido.cliente?.email || "-"}
                </Typography>
              </Box>

              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Entrega
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{modalPedido.direccion?.nombre || "-"}:</strong> {modalPedido.direccion?.direccion || "-"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Referencia:</strong> {modalPedido.direccion?.referencia || "-"}
                </Typography>
                {modalPedido.direccion?.mapa_url && (
                  <Link href={modalPedido.direccion.mapa_url} target="_blank" rel="noopener noreferrer" variant="body2">
                    Ver en el mapa
                  </Link>
                )}
              </Box>

              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Pedido
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  <strong>Fecha:</strong> {formatDate(modalPedido.fecha_creacion)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Pago:</strong> {modalPedido.metodo_pago || "-"}, al recibir
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Estado en Teros:</strong> {estadoTeros(modalPedido.estado_teros).etiqueta}
                </Typography>
                {modalPedido.estado_teros !== "sincronizado" && modalPedido.teros_message && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>Mensaje de Teros:</strong> {modalPedido.teros_message}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  <strong>Observación:</strong> {modalPedido.observacion_general || "-"}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Productos
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell>Código</TableCell>
                      <TableCell align="right">Cant.</TableCell>
                      <TableCell>Unidad</TableCell>
                      <TableCell align="right">P. unit.</TableCell>
                      <TableCell align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(modalPedido.detalles ?? []).map((detalle, indice) => (
                      <React.Fragment key={`${detalle.codigo_producto}-${indice}`}>
                        <TableRow>
                          <TableCell>{detalle.nombre_producto || "-"}</TableCell>
                          <TableCell>{detalle.codigo_producto || "-"}</TableCell>
                          <TableCell align="right">{formatQuantity(detalle.cantidad)}</TableCell>
                          <TableCell>{detalle.codigo_unidad || "-"}</TableCell>
                          <TableCell align="right">{formatCurrency(detalle.precio_descuento ?? detalle.precio)}</TableCell>
                          <TableCell align="right">{formatCurrency(detalle.subtotal)}</TableCell>
                        </TableRow>
                        {detalle.observacion_usuario && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ py: 0.5 }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ fontStyle: "italic", display: "block", whiteSpace: "pre-wrap", wordBreak: "break-word", pb: 1 }}
                              >
                                {detalle.observacion_usuario}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {formatCurrency(modalPedido.total)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
              <Button onClick={() => setModalPedido(null)} variant="contained">
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default PedidosDomicilio;
